import { PeriodKpi } from "@/api/periodData";
import { STAT_GROUPS, STAT_LABELS, formatValue, getPeriodLabel } from "./periodDataUtils";
import { getTeamLogo } from "@/lib/utils";

interface SummaryGroupCardProps {
    periods: number[];
    homeTeamPeriods: PeriodKpi[];
    awayTeamPeriods: PeriodKpi[];
    groupLabel: string;
    fields: (keyof Omit<PeriodKpi, "period" | "teamId" | "matchId">)[];
    homeTeamName?: string;
    awayTeamName?: string;
    homeLogo?: string | null;
    awayLogo?: string | null;
}

const SummaryGroupCard = ({
    periods,
    homeTeamPeriods,
    awayTeamPeriods,
    fields,
    homeTeamName,
    awayTeamName,
    homeLogo,
    awayLogo,
}: SummaryGroupCardProps) => (
    <div className="bg-surface-container-lowest rounded-xl overflow-hidden shadow-sm flex-1">
        <div className="block md:hidden">
            <div className="px-3 py-1.5 flex items-center gap-2" style={{ background: "#050066" }}>
                {homeLogo && <img src={homeLogo} alt="" className="h-4 w-4 object-contain" />}
                <span className="text-[9px] font-black uppercase tracking-widest text-white">
                    {homeTeamName ?? "Home"}
                </span>
            </div>
            <table className="w-full text-left">
                <thead>
                    <tr className="bg-surface-container-high">
                        <th className="px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-on-surface-variant">
                            Stat
                        </th>
                        {periods.map((p) => (
                            <th
                                key={p}
                                className="px-2 py-1.5 text-[9px] font-black uppercase tracking-widest text-center"
                                style={{ color: "#050066" }}
                            >
                                {getPeriodLabel(p)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {fields.map((field, i) => (
                        <tr key={field} className={i % 2 === 0 ? "bg-surface-container-low" : "bg-surface"}>
                            <td className="px-3 py-1.5 text-[10px] font-bold text-on-surface-variant">
                                {STAT_LABELS[field]}
                            </td>
                            {periods.map((p) => {
                                const pd = homeTeamPeriods.find((x) => x.period === p);
                                const val = pd?.[field];
                                const awayPd = awayTeamPeriods.find((x) => x.period === p);
                                const awayVal = awayPd?.[field];
                                const isWinner = val !== undefined && awayVal !== undefined && val > awayVal;
                                return (
                                    <td
                                        key={p}
                                        className="px-2 py-1.5 text-center tabular-nums text-xs font-semibold font-headline"
                                        style={
                                            isWinner
                                                ? { color: "#050066" }
                                                : { color: "var(--color-on-surface-variant)" }
                                        }
                                    >
                                        {val !== undefined ? formatValue(val, field) : "—"}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="h-px bg-outline-variant/20" />

            <div className="px-3 py-1.5 flex items-center gap-2" style={{ background: "#ba0a06" }}>
                {awayLogo && <img src={awayLogo} alt="" className="h-4 w-4 object-contain" />}
                <span className="text-[9px] font-black uppercase tracking-widest text-white">
                    {awayTeamName ?? "Away"}
                </span>
            </div>
            <table className="w-full text-left">
                <thead>
                    <tr className="bg-surface-container-high">
                        <th className="px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-on-surface-variant">
                            Stat
                        </th>
                        {periods.map((p) => (
                            <th
                                key={p}
                                className="px-2 py-1.5 text-[9px] font-black uppercase tracking-widest text-center"
                                style={{ color: "#ba0a06" }}
                            >
                                {getPeriodLabel(p)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {fields.map((field, i) => (
                        <tr key={field} className={i % 2 === 0 ? "bg-surface-container-low" : "bg-surface"}>
                            <td className="px-3 py-1.5 text-[10px] font-bold text-on-surface-variant">
                                {STAT_LABELS[field]}
                            </td>
                            {periods.map((p) => {
                                const pd = awayTeamPeriods.find((x) => x.period === p);
                                const val = pd?.[field];
                                const homePd = homeTeamPeriods.find((x) => x.period === p);
                                const homeVal = homePd?.[field];
                                const isWinner = val !== undefined && homeVal !== undefined && val > homeVal;
                                return (
                                    <td
                                        key={p}
                                        className="px-2 py-1.5 text-center tabular-nums text-xs font-semibold font-headline"
                                        style={
                                            isWinner
                                                ? { color: "#ba0a06" }
                                                : { color: "var(--color-on-surface-variant)" }
                                        }
                                    >
                                        {val !== undefined ? formatValue(val, field) : "—"}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
                <thead>
                    <tr className="bg-surface-container-high">
                        <th className="px-4 py-2 text-[9px] font-black uppercase tracking-widest text-on-surface-variant w-36">
                            Stat
                        </th>
                        <th className="w-px bg-outline-variant/30 p-0" />
                        {periods.map((p) => (
                            <th
                                key={`home-${p}`}
                                className="px-3 py-2 text-[9px] font-black uppercase tracking-widest text-center"
                                style={{ color: "#050066" }}
                            >
                                <div className="flex flex-col items-center gap-0.5">
                                    {homeLogo && <img src={homeLogo} alt="" className="h-4 w-4 object-contain" />}
                                    <span>{getPeriodLabel(p)}</span>
                                </div>
                            </th>
                        ))}
                        <th className="px-1 py-2 w-px bg-surface-container-high" />
                        {periods.map((p) => (
                            <th
                                key={`away-${p}`}
                                className="px-3 py-2 text-[9px] font-black uppercase tracking-widest text-center"
                                style={{ color: "#ba0a06" }}
                            >
                                <div className="flex flex-col items-center gap-0.5">
                                    {awayLogo && <img src={awayLogo} alt="" className="h-4 w-4 object-contain" />}
                                    <span>{getPeriodLabel(p)}</span>
                                </div>
                            </th>
                        ))}
                    </tr>
                    <tr className="bg-surface-container">
                        <td className="px-4 py-1" />
                        <td className="w-px p-0" />
                        <td
                            colSpan={periods.length}
                            className="px-3 py-1 text-[8px] font-black uppercase tracking-widest text-center"
                            style={{ color: "#050066" }}
                        >
                            {homeTeamName ?? "Home"}
                        </td>
                        <td className="px-1 py-1 w-px" />
                        <td
                            colSpan={periods.length}
                            className="px-3 py-1 text-[8px] font-black uppercase tracking-widest text-center"
                            style={{ color: "#ba0a06" }}
                        >
                            {awayTeamName ?? "Away"}
                        </td>
                    </tr>
                </thead>
                <tbody>
                    {fields.map((field, i) => (
                        <tr key={field} className={i % 2 === 0 ? "bg-surface-container-low" : "bg-surface"}>
                            <td className="px-4 py-2 text-xs font-bold text-on-surface-variant">
                                {STAT_LABELS[field]}
                            </td>
                            <td className="w-px bg-outline-variant/10 p-0" />
                            {periods.map((p) => {
                                const pd = homeTeamPeriods.find((x) => x.period === p);
                                const val = pd?.[field];
                                const awayPd = awayTeamPeriods.find((x) => x.period === p);
                                const awayVal = awayPd?.[field];
                                const isWinner = val !== undefined && awayVal !== undefined && val > awayVal;
                                return (
                                    <td
                                        key={p}
                                        className="px-3 py-2 text-center tabular-nums font-headline text-sm font-semibold"
                                        style={
                                            isWinner
                                                ? { color: "#050066" }
                                                : { color: "var(--color-on-surface-variant)" }
                                        }
                                    >
                                        {val !== undefined ? formatValue(val, field) : "—"}
                                    </td>
                                );
                            })}
                            <td className="w-px bg-outline-variant/10" />
                            {periods.map((p) => {
                                const pd = awayTeamPeriods.find((x) => x.period === p);
                                const val = pd?.[field];
                                const homePd = homeTeamPeriods.find((x) => x.period === p);
                                const homeVal = homePd?.[field];
                                const isWinner = val !== undefined && homeVal !== undefined && val > homeVal;
                                return (
                                    <td
                                        key={p}
                                        className="px-3 py-2 text-center tabular-nums font-headline text-sm font-semibold"
                                        style={
                                            isWinner
                                                ? { color: "#ba0a06" }
                                                : { color: "var(--color-on-surface-variant)" }
                                        }
                                    >
                                        {val !== undefined ? formatValue(val, field) : "—"}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

interface PeriodSummaryTableProps {
    periods: number[];
    homeTeamPeriods: PeriodKpi[];
    awayTeamPeriods: PeriodKpi[];
    homeTeamName?: string;
    awayTeamName?: string;
}

export const PeriodSummaryTable = ({
    periods,
    homeTeamPeriods,
    awayTeamPeriods,
    homeTeamName,
    awayTeamName,
}: PeriodSummaryTableProps) => {
    const homeLogo = homeTeamName ? (getTeamLogo(homeTeamName) ?? null) : null;
    const awayLogo = awayTeamName ? (getTeamLogo(awayTeamName) ?? null) : null;

    return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {STAT_GROUPS.map((group) => (
                <div key={group.label} className="flex flex-col gap-3">
                    <h3 className="text-lg font-headline font-bold shrink-0" style={{ color: "#050066" }}>
                        {group.label}
                    </h3>
                    <SummaryGroupCard
                        periods={periods}
                        homeTeamPeriods={homeTeamPeriods}
                        awayTeamPeriods={awayTeamPeriods}
                        groupLabel={group.label}
                        fields={group.fields}
                        homeTeamName={homeTeamName}
                        awayTeamName={awayTeamName}
                        homeLogo={homeLogo}
                        awayLogo={awayLogo}
                    />
                </div>
            ))}
        </div>
    );
};
